/**
 * Frontier routines — listing + lifecycle actions (REQ-FRT-016 §2).
 *
 * Action × state matrix (entrega 1): activate (#edit), pause/resume
 * (#operate), archive (#edit). Actions outside the matrix or without the
 * required permission render disabled with an explanatory tooltip.
 */

import { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';

import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useAgents } from '@/features/agents/hooks/useAgents';
import { StatusChip } from '../components/FrontierGuard';
import { LaunchRoutineDialog } from '../components/LaunchRoutineDialog';
import { RoutineEditorDialog } from '../components/RoutineEditorDialog';
import {
  useAcknowledgeEmergency,
  useConsentActions,
  useFrontierConsents,
  useFrontierRoutines,
  useRoutineLifecycle,
} from '../hooks/useFrontier';
import {
  ROUTINE_ACTIVATABLE,
  ROUTINE_ARCHIVABLE,
  ROUTINE_PAUSABLE,
  ROUTINE_RESUMABLE,
  type FrontierRoutine,
} from '../types';

interface ActionSpec {
  id: 'activate' | 'pause' | 'resume' | 'archive';
  allowed: boolean;
  permitted: boolean;
}

export default function FrontierRoutinesPage() {
  const { t } = useI18n();
  const canEdit = useModuleAccess('frontier', 'edit');
  const canOperate = useModuleAccess('frontier', 'operate');
  const canUse = useModuleAccess('frontier', 'use');
  const { data, isLoading, isError } = useFrontierRoutines();
  // Presentation field: the routines table shows the agent NAME, never its
  // internal id (frontend-development-guide §8.2).
  const { data: agents } = useAgents({ pageSize: 100 });
  const lifecycle = useRoutineLifecycle();
  const consents = useFrontierConsents();
  const consentActions = useConsentActions();
  const acknowledge = useAcknowledgeEmergency();
  const [ackTarget, setAckTarget] = useState<FrontierRoutine | null>(null);
  const [ackReason, setAckReason] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [launchTarget, setLaunchTarget] = useState<FrontierRoutine | null>(null);

  const agentNames = new Map((agents ?? []).map((agent) => [agent.id, agent.name]));
  const agentLabel = (agentId: string) => agentNames.get(agentId) ?? '—';

  const overlapLabel = (policy: string): string => {
    const key = `frontier.routines.overlap.${policy}`;
    const label = t(key);
    return label === key ? policy : label;
  };

  const actionsFor = (routine: FrontierRoutine): ActionSpec[] => [
    {
      id: 'activate',
      allowed: ROUTINE_ACTIVATABLE.includes(routine.status),
      permitted: canEdit,
    },
    {
      id: 'pause',
      allowed: ROUTINE_PAUSABLE.includes(routine.status),
      permitted: canOperate,
    },
    {
      id: 'resume',
      allowed: ROUTINE_RESUMABLE.includes(routine.status),
      permitted: canOperate,
    },
    {
      id: 'archive',
      allowed: ROUTINE_ARCHIVABLE.includes(routine.status),
      permitted: canEdit,
    },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="frontier-routines">
      <PageHeader
        icon={CalendarClock}
        title={t('frontier.routines.title')}
        actions={
          canEdit ? (
            <Button
              data-testid="frontier-new-routine"
              onClick={() => {
                setEditingId(null);
                setEditorOpen(true);
              }}
            >
              + {t('frontier.routines.new')}
            </Button>
          ) : undefined
        }
        testId="frontier-routines-header"
      />

      {lifecycle.isError && (
        <p className="text-sm text-red-600" role="alert">
          {t('frontier.actions.failed')}
        </p>
      )}

      {(consents.data ?? []).length > 0 && (
        <Card>
          <CardContent className="space-y-3 pt-6" data-testid="frontier-consents">
            <h2 className="text-lg font-semibold">{t('frontier.consents.title')}</h2>
            {consentActions.isError && (
              <p className="text-sm text-red-600" role="alert">
                {t('frontier.consents.error')}
              </p>
            )}
            {(consents.data ?? []).map((consent) => (
              <div
                key={consent.routine_id}
                className="flex items-center justify-between gap-3"
                data-testid={`consent-${consent.routine_id}`}
              >
                <div className="min-w-0">
                  <span className="truncate text-sm font-medium">{consent.routine_name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    {t(`frontier.consents.${consent.status}`)}
                  </span>
                </div>
                <div className="flex gap-1">
                  {consent.status === 'pending' && (
                    <Button
                      size="sm"
                      data-testid={`consent-accept-${consent.routine_id}`}
                      disabled={consentActions.isPending}
                      onClick={() =>
                        consentActions.mutate({ routineId: consent.routine_id, action: 'accept' })
                      }
                    >
                      {t('frontier.consents.accept')}
                    </Button>
                  )}
                  {consent.status === 'active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`consent-revoke-${consent.routine_id}`}
                      disabled={consentActions.isPending}
                      onClick={() =>
                        consentActions.mutate({ routineId: consent.routine_id, action: 'revoke' })
                      }
                    >
                      {t('frontier.consents.revoke')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
              <Spinner size="sm" />
            </div>
          ) : isError ? (
            <p className="py-6 text-sm text-red-600" role="alert">
              {t('frontier.actions.failed')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('frontier.routines.colName')}</TableHead>
                  <TableHead>{t('frontier.routines.colStatus')}</TableHead>
                  <TableHead>{t('frontier.routines.colOverlap')}</TableHead>
                  <TableHead>{t('frontier.routines.colPausedReason')}</TableHead>
                  <TableHead>{t('frontier.routines.colAgent')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data ?? []).length === 0 && (
                  <TableEmpty colSpan={6} title={t('frontier.routines.empty')} />
                )}
                {(data ?? []).map((routine) => (
                  <TableRow key={routine.id} data-testid={`routine-row-${routine.id}`}>
                    <TableCell className="font-medium">{routine.name}</TableCell>
                    <TableCell>
                      <StatusChip
                        kind="routine"
                        value={routine.status}
                        label={t(`frontier.routines.status.${routine.status}`)}
                      />
                    </TableCell>
                    <TableCell>{overlapLabel(routine.overlap_policy)}</TableCell>
                    <TableCell>{routine.paused_reason ?? '—'}</TableCell>
                    <TableCell data-testid={`routine-agent-${routine.id}`}>
                      {agentLabel(routine.agent_id)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {routine.status === 'auto_paused' &&
                          (routine.paused_reason ?? '').startsWith('emergency:') &&
                          (canOperate || canEdit) && (
                            <Button
                              size="sm"
                              variant="destructive"
                              data-testid={`routine-ack-${routine.id}`}
                              onClick={() => {
                                setAckTarget(routine);
                                setAckReason('');
                              }}
                            >
                              {t('frontier.routines.ack')}
                            </Button>
                          )}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!canEdit}
                          data-testid={`routine-edit-${routine.id}`}
                          onClick={() => {
                            setEditingId(routine.id);
                            setEditorOpen(true);
                          }}
                        >
                          {t('frontier.routines.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!canUse}
                          data-testid={`routine-launch-${routine.id}`}
                          onClick={() => setLaunchTarget(routine)}
                        >
                          {t('frontier.routines.launch')}
                        </Button>
                        {actionsFor(routine).map((action) => {
                          const enabled = action.allowed && action.permitted;
                          const title = !action.allowed
                            ? t('frontier.routines.actions.phase')
                            : undefined;
                          return (
                            <Button
                              key={action.id}
                              size="sm"
                              variant="outline"
                              disabled={!enabled || lifecycle.isPending}
                              title={title}
                              data-testid={`routine-${action.id}-${routine.id}`}
                              onClick={() =>
                                lifecycle.mutate({ routineId: routine.id, action: action.id })
                              }
                            >
                              {t(`frontier.routines.actions.${action.id}`)}
                            </Button>
                          );
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RoutineEditorDialog
        open={editorOpen}
        routineId={editingId}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setEditingId(null);
        }}
      />
      <LaunchRoutineDialog
        routine={launchTarget}
        onOpenChange={(open) => {
          if (!open) setLaunchTarget(null);
        }}
      />

      <Dialog open={!!ackTarget} onOpenChange={(open) => !open && setAckTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('frontier.routines.ackTitle')}
              {ackTarget ? ` · ${ackTarget.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-medium" htmlFor="ack-reason">
              {t('frontier.routines.ackReason')}
            </label>
            <textarea
              id="ack-reason"
              data-testid="ack-reason"
              rows={3}
              className="w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-900"
              value={ackReason}
              onChange={(event) => setAckReason(event.target.value)}
            />
            {acknowledge.isError && (
              <p className="text-sm text-red-600" role="alert">
                {t('frontier.routines.ackError')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckTarget(null)} disabled={acknowledge.isPending}>
              {t('frontier.editor.cancel')}
            </Button>
            <Button
              disabled={acknowledge.isPending || !ackReason.trim()}
              data-testid="ack-send"
              onClick={() => {
                if (!ackTarget) return;
                acknowledge.mutate(
                  { routineId: ackTarget.id, reason: ackReason.trim() },
                  { onSuccess: () => setAckTarget(null) },
                );
              }}
            >
              {t('frontier.routines.ackSend')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
